import { api } from './api'
import type { levelFor } from '../../shared/stash'
import type { IconName } from '../components/Icon'

/** Client side of services/api/src/stash.ts. Amounts in thousandths, money in cents. */

export type Unit = 'g' | 'each'

export interface Batch {
  id: string
  name: string
  category: string
  unit: Unit
  amount_milli: number
  cost_cents: number
  notes: string | null
  archived: number
  waste_milli: number
  finished_at: number | null
  bought_at: number
  packed_milli: number
  sold_milli: number
  ready_count: number
  sold_count: number
  sold_cents: number
}

export interface Pack {
  id: string
  batch_id: string
  batch_name: string
  unit: Unit
  category: string
  label: string
  amount_milli: number
  price_cents: number
  status: 'ready' | 'sold'
  sold_cents: number | null
  sold_note: string | null
  sold_at: number | null
  cost_cents: number
}

export interface Badge { key: string; icon: IconName; title: string; hint: string; earned: boolean }

export interface Summary {
  xp: number
  level: ReturnType<typeof levelFor>
  streak: { current: number; best: number; soldToday: boolean }
  today: { cents: number; count: number }
  weekCents: number
  week: { day: string; cents: number }[]
  revenueCents: number
  profitCents: number
  spentCents: number
  shelf: { count: number; cents: number }
  sales: number
  badges: Badge[]
}

export interface SaleResult {
  sold: number
  soldCents: number
  xp: number
  leveledUp: Summary['level'] | null
  newBadges: Badge[]
  summary: Summary
}

const send = (method: string, body?: unknown): RequestInit => ({ method, body: body === undefined ? undefined : JSON.stringify(body) })

export const stash = {
  summary: () => api<Summary>('/api/stash/summary'),
  batches: () => api<{ batches: Batch[] }>('/api/stash/batches').then((r) => r.batches),
  packages: (status?: 'ready' | 'sold') => api<{ packages: Pack[] }>(`/api/stash/packages${status ? `?status=${status}` : ''}`).then((r) => r.packages),
  createBatch: (b: { name: string; category: string; unit: Unit; amountMilli: number; costCents: number; notes?: string }) =>
    api<{ batch: Batch; xp: number }>('/api/stash/batches', send('POST', b)),
  updateBatch: (id: string, b: Partial<{ name: string; notes: string | null; costCents: number; amountMilli: number; archived: boolean }>) =>
    api<{ batch: Batch }>(`/api/stash/batches/${id}`, send('PATCH', b)),
  finish: (id: string) => api<{ batch: Batch; wroteOffMilli: number }>(`/api/stash/batches/${id}/finish`, send('POST')),
  reopen: (id: string) => api<{ batch: Batch }>(`/api/stash/batches/${id}/reopen`, send('POST')),
  deleteBatch: (id: string) => api(`/api/stash/batches/${id}`, send('DELETE')),
  split: (id: string, s: { count: number; amountMilli: number; priceCents: number; label?: string }) =>
    api<{ made: number; xp: number; batch: Batch }>(`/api/stash/batches/${id}/split`, send('POST', s)),
  updatePackage: (id: string, p: { priceCents?: number; label?: string }) => api<{ package: Pack }>(`/api/stash/packages/${id}`, send('PATCH', p)),
  deletePackage: (id: string) => api(`/api/stash/packages/${id}`, send('DELETE')),
  /** Sells every id given, at soldCents EACH. One package is just a list of one. */
  sell: (ids: string[], s: { soldCents?: number; note?: string }) => api<SaleResult>('/api/stash/sell', send('POST', { ids, ...s })),
  unsell: (id: string) => api<{ package: Pack }>(`/api/stash/packages/${id}/unsell`, send('POST')),
}

/** "$40" or "40.50" typed by a human → whole cents, or null if it is not money. */
export function toCents(input: string): number | null {
  const n = Number(input.replace(/[$,\s]/g, ''))
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null
}

export const CATEGORIES = [
  { key: 'flower', icon: 'bud', label: 'Flower' },
  { key: 'preroll', icon: 'joint', label: 'Pre-roll' },
  { key: 'edible', icon: 'gummy', label: 'Edible' },
  { key: 'vape', icon: 'vape', label: 'Vape' },
  { key: 'concentrate', icon: 'drop', label: 'Concentrate' },
  { key: 'other', icon: 'box', label: 'Other' },
] as const satisfies readonly { key: string; icon: IconName; label: string }[]

export const iconFor = (category: string): IconName => CATEGORIES.find((c) => c.key === category)?.icon ?? 'box'
